// covered-by: frontends/carrie/src/routes/ask-puntt/components/__tests__/ResizeHandle.test.tsx
import {
  type RefObject,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import {
  PanelResizeHandle,
  type ImperativePanelGroupHandle,
} from "react-resizable-panels";

import {
  RESIZE_HANDLE_GROUP_CLASS,
  ResizeHandleLine,
} from "./ResizeHandleLine";

export type ResizeHandleProps = {
  /** Unique id for the handle; surfaced as data-testid + aria-controls anchor. */
  id: string;
  /** Pane group ref, so ESC during drag can revert to the pre-drag layout and double-click can reset. */
  panelGroupRef: RefObject<ImperativePanelGroupHandle | null>;
  /** Default layout to snap back to on double-click reset. */
  defaultLayout: number[];
  /**
   * Stable signal that should abort an in-progress drag (e.g., the chat
   * route is switching tickets). When this value changes mid-drag, we
   * dispatch a synthetic pointerup so react-resizable-panels finishes the
   * drag cleanly instead of leaving the body cursor/no-select state stuck.
   */
  abortSignal?: string | number | null;
  /**
   * Optional callback so the parent route can suppress competing CSS
   * transitions while a drag is in flight. Receives true on drag-start,
   * false on drag-end / ESC-cancel / unmount.
   */
  onDraggingChange?: (dragging: boolean) => void;
};

function resizeHandleAriaLabel(
  t: (key: string, options?: Record<string, unknown>) => string,
  id: string,
): string {
  switch (id) {
    case "chat-right":
      return t("resizeHandle.ariaLabel.chatRight");
    case "context-artwork":
      return t("resizeHandle.ariaLabel.contextArtwork");
    case "feed-rail":
      return t("resizeHandle.ariaLabel.feedRail");
    default:
      return t("resizeHandle.ariaLabel.generic", { id });
  }
}

export function ResizeHandle({
  id,
  panelGroupRef,
  defaultLayout,
  abortSignal,
  onDraggingChange,
}: ResizeHandleProps) {
  const { t } = useTranslation("ui");
  const [isDragging, setIsDragging] = useState(false);
  const preDragLayoutRef = useRef<number[] | null>(null);
  const hintId = useId();

  // Capture the layout at drag start so ESC can revert.
  const handleDragging = useCallback(
    (dragging: boolean) => {
      setIsDragging(dragging);
      onDraggingChange?.(dragging);
      if (dragging) {
        preDragLayoutRef.current = panelGroupRef.current?.getLayout() ?? null;
        document.body.style.cursor = "col-resize";
        document.body.style.userSelect = "none";
      } else {
        preDragLayoutRef.current = null;
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
      }
    },
    [onDraggingChange, panelGroupRef],
  );

  // ESC during drag → revert.
  useEffect(() => {
    if (!isDragging) return;
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key !== "Escape") return;
      const pre = preDragLayoutRef.current;
      if (pre && panelGroupRef.current) {
        panelGroupRef.current.setLayout(pre);
      }
      // Synthesize pointerup so react-resizable-panels exits its drag state cleanly.
      window.dispatchEvent(
        new PointerEvent("pointerup", { bubbles: true, cancelable: true }),
      );
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isDragging, panelGroupRef]);

  // Abort signal change mid-drag (ticket switch, route change) → end the drag.
  useEffect(() => {
    if (!isDragging) return;
    window.dispatchEvent(
      new PointerEvent("pointerup", { bubbles: true, cancelable: true }),
    );
    // intentionally only depend on the abort signal — we want this to fire
    // when the signal changes, not when isDragging flips back to false.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abortSignal]);

  // Clean up body styles if the handle unmounts mid-drag (e.g., layout flips
  // 3-pane → chat-only because the ticket was closed).
  useEffect(() => {
    return () => {
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, []);

  const handleDoubleClick = useCallback(() => {
    panelGroupRef.current?.setLayout(defaultLayout);
  }, [defaultLayout, panelGroupRef]);

  return (
    <PanelResizeHandle
      id={id}
      onDragging={handleDragging}
      hitAreaMargins={{ coarse: 24, fine: 12 }}
      // Zero-width track: panes sit flush and the shared line overlays the seam,
      // so thickening on hover never reflows a pane. Hit target comes from
      // hitAreaMargins, not track width.
      className={`${RESIZE_HANDLE_GROUP_CLASS} relative flex h-full w-0`}
      data-testid={`askpuntt-resize-handle-${id}`}
      aria-label={resizeHandleAriaLabel(t, id)}
      aria-describedby={hintId}
      onDoubleClick={handleDoubleClick}
    >
      <ResizeHandleLine
        active={isDragging}
        orientation="vertical"
        testId={`askpuntt-resize-handle-divider-${id}`}
      />
      <span id={hintId} className="sr-only">
        {t("resizeHandle.keyboardHint")}
      </span>
    </PanelResizeHandle>
  );
}
